import { useEffect, useMemo, useRef, useState, useId } from 'react';
import type { SelectOption } from './Select';
import { labelStartsWith, sortOptionsByLabel } from './optionOrder';

/**
 * SearchableSelect — a typeahead/combobox single-select for long option lists.
 *
 * A plain <select> is fine for a handful of options, but the consultant / client
 * / property pickers on a Consultant Deal reference contact/client/property lists
 * that can run to hundreds of rows. This atom lets the operator filter by typing
 * while keeping the token design system (dark-first surfaces, brand focus ring).
 *
 * Pure props in / value out — the consumer owns the option list and the selected
 * value; SearchableSelect does no data fetching. `value === null` means unselected.
 *
 * Order: alphabetical by label within each group (./optionOrder), unless the consumer
 * passes `preserveOrder` for a list whose order is its meaning.
 *
 * Keyboard: the first letter typed into an empty box jumps to the first option starting
 * with it, as a native select does, and leaves the whole list in view; a second character
 * filters by substring as before. ArrowUp/Down move the highlight, Enter picks the
 * highlighted option, Escape closes. Mouse: click an option to pick it.
 */

/** An option that may belong to a labeled group (rendered as a non-selectable header). */
export interface GroupedSelectOption extends SelectOption {
  group?: string;
}

export interface SearchableSelectProps {
  options: GroupedSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** When set, a clearing option is rendered at the top of the list (maps to null). */
  emptyLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  'aria-label'?: string;
  /** Show the options in the order given instead of alphabetically. */
  preserveOrder?: boolean;
}

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  emptyLabel,
  disabled,
  invalid,
  id,
  'aria-label': ariaLabel,
  preserveOrder = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const listId = useId();
  const listRef = useRef<HTMLUListElement | null>(null);

  const ordered = useMemo(
    () => (preserveOrder ? options : sortOptionsByLabel(options)),
    [options, preserveOrder],
  );

  const selected = useMemo(
    () => options.find(o => String(o.value) === String(value)) ?? null,
    [options, value],
  );

  // First-letter jump: exactly one character typed, and some option starts with it. The
  // list stays whole and the highlight moves; with no such option the character filters.
  const jumpLetter = query.length === 1 && query.trim() !== '' && ordered.some(o => labelStartsWith(o.label, query))
    ? query
    : null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || jumpLetter) return ordered;
    return ordered.filter(o => o.label.toLowerCase().includes(q));
  }, [ordered, query, jumpLetter]);

  // The clearing row (index 0) is prepended when emptyLabel is set. Grouped options keep
  // their group order and each group's header row is emitted once, only while the group
  // still has a match for the current query (a header over nothing would read as an
  // empty category, which is worse than no header).
  const rows: Array<{ value: string | null; label: string; header?: boolean }> = useMemo(() => {
    const base: Array<{ value: string | null; label: string; header?: boolean }> = [];
    let lastGroup: string | undefined;
    for (const o of filtered) {
      if (o.group && o.group !== lastGroup) { base.push({ value: null, label: o.group, header: true }); lastGroup = o.group; }
      base.push({ value: String(o.value), label: o.label });
    }
    return emptyLabel ? [{ value: null, label: emptyLabel }, ...base] : base;
  }, [filtered, emptyLabel]);
  const selectable = (i: number) => rows[i] && !rows[i].header;
  const optionId = (i: number) => `${listId}-option-${i}`;

  useEffect(() => {
    if (!jumpLetter) return;
    const target = rows.findIndex(r => !r.header && r.value !== null && labelStartsWith(r.label, jumpLetter));
    if (target >= 0) setHighlight(target);
  }, [jumpLetter, rows]);

  // Keep the highlighted row in view as the jump or the arrow keys move it.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-row-index="${highlight}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, highlight]);
  const step = (from: number, dir: 1 | -1) => {
    let i = from + dir;
    while (i >= 0 && i < rows.length && !selectable(i)) i += dir;
    return i >= 0 && i < rows.length ? i : from;
  };

  function openList() {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setHighlight(0);
  }

  function pick(row: { value: string | null; label: string; header?: boolean }) {
    if (row.header) return;
    onChange(row.value);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { openList(); return; }
      setHighlight(h => step(h, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => step(h, -1));
    } else if (e.key === 'Enter') {
      if (open && rows[highlight]) { e.preventDefault(); pick(rows[highlight]); }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  const inputValue = open ? query : (selected?.label ?? '');

  return (
    <div style={{ position: 'relative' }}>
      <input
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        // The highlighted row is announced while focus stays in the input, which
        // is how a combobox's arrow keys are heard (ARIA combobox pattern).
        aria-activedescendant={open && selectable(highlight) ? optionId(highlight) : undefined}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        autoComplete="off"
        disabled={disabled}
        value={inputValue}
        placeholder={selected ? selected.label : placeholder}
        onFocus={openList}
        onBlur={() => {
          // Defer close so an option's onClick can fire before the list unmounts.
          blurTimer.current = setTimeout(() => setOpen(false), 120);
        }}
        onChange={e => { setQuery(e.target.value); setOpen(true); setHighlight(emptyLabel ? 1 : 0); }}
        onKeyUp={() => { if (open && rows[highlight] && rows[highlight].header) setHighlight(h => step(h, 1)); }}
        onKeyDown={onKeyDown}
        style={{
          fontFamily: 'inherit',
          fontSize: 'var(--fs-md)',
          padding: '8px 12px',
          border: `1px solid ${invalid ? 'var(--color-status-red)' : 'var(--color-border)'}`,
          borderRadius: 'var(--r-md)',
          background: 'var(--color-card)',
          color: 'var(--color-ink)',
          width: '100%',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          onMouseDown={e => {
            // Keep focus on the input (prevents the blur-close from racing the click).
            e.preventDefault();
            if (blurTimer.current) clearTimeout(blurTimer.current);
          }}
          style={{
            position: 'absolute',
            zIndex: 20,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            margin: 0,
            padding: 'var(--sp-1)',
            listStyle: 'none',
            maxHeight: 240,
            overflowY: 'auto',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--shadow-md, 0 8px 24px rgba(0,0,0,0.24))',
          }}
        >
          {rows.length === 0 && (
            <li role="presentation" style={{ padding: 'var(--sp-2) var(--sp-3)', color: 'var(--color-ink-3)', fontSize: 'var(--fs-sm)' }}>
              No matches
            </li>
          )}
          {rows.map((row, i) => {
            if (row.header) {
              return (
                <li key={`__group__${row.label}`} role="presentation" aria-hidden="true" style={{
                  padding: 'var(--sp-2) var(--sp-3) var(--sp-1)', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
                  textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)',
                }}>{row.label}</li>
              );
            }
            const isSel = row.value === (value ?? null);
            const isHi = i === highlight;
            return (
              // The option itself is the click target. It is not a nested <button>:
              // a focusable control inside an option is invalid ARIA, and focus
              // belongs in the input (the highlighted option is announced through
              // aria-activedescendant instead). Keyboard selection is the input's
              // onKeyDown, so the option's click has no key handler of its own.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={row.value ?? '__empty__'}
                id={optionId(i)}
                role="option"
                aria-selected={isSel}
                data-row-index={i}
                onClick={() => pick(row)}
                onMouseEnter={() => setHighlight(i)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  padding: 'var(--sp-2) var(--sp-3)',
                  borderRadius: 'var(--r-sm)',
                  fontSize: 'var(--fs-sm)',
                  fontFamily: 'inherit',
                  background: isHi ? 'var(--brand-primary-bg)' : 'transparent',
                  // The keyboard highlight also gets an outline, so it does not rely
                  // on the pale brand tint alone to be seen.
                  outline: isHi ? '1px solid var(--brand-primary)' : 'none',
                  outlineOffset: -1,
                  color: row.value === null ? 'var(--color-ink-3)' : 'var(--color-ink)',
                  fontWeight: isSel ? 'var(--fw-semibold)' : 400,
                }}
              >
                {row.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
